import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons
const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const endIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Orange icon for intermediate stops
const stopIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to fit bounds when route changes
const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (!bounds || bounds.length < 2) return;
    
    let timeoutId;
    let isSubscribed = true;
    
    try {
      timeoutId = setTimeout(() => {
        if (isSubscribed && map && map._container && map._loaded) {
          try {
            map.fitBounds(bounds, { 
              padding: [50, 50],
              maxZoom: 10,
              animate: false,
              duration: 0.1
            });
          } catch (e) {
            console.warn('Could not fit bounds:', e.message);
          }
        }
      }, 200);
    } catch (error) {
      console.error('Error fitting bounds:', error);
    }
    
    return () => {
      isSubscribed = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [JSON.stringify(bounds), map]);
  return null;
};

/**
 * TransportMap - Carte avec support multi-étapes
 * @param {string} departure - Adresse de départ
 * @param {string} arrival - Adresse d'arrivée
 * @param {Array} stops - Étapes intermédiaires [{address, city, coordinates, order}]
 * @param {Function} onDistanceCalculated - Callback avec la distance totale
 */
const TransportMap = ({ departure, arrival, stops = [], onDistanceCalculated, showDistanceBadge = true }) => {
  const [departureCoords, setDepartureCoords] = useState(null);
  const [arrivalCoords, setArrivalCoords] = useState(null);
  const [stopsCoords, setStopsCoords] = useState([]);
  const [routeSegments, setRouteSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapBounds, setMapBounds] = useState(null);
  const [totalDistance, setTotalDistance] = useState(null);

  // Geocode a single address
  const geocodeAddress = async (address) => {
    if (!address) return null;
    try {
      const response = await axios.get(`${API}/geocoding/search`, {
        params: { q: address, limit: 1 }
      });
      if (response.data.success && response.data.results.length > 0) {
        const result = response.data.results[0];
        return [result.lat, result.lon];
      }
    } catch (error) {
      console.error('Geocoding error for:', address, error.message);
    }
    return null;
  };

  // Geocode all addresses (departure, stops, arrival)
  useEffect(() => {
    const geocodeAll = async () => {
      setLoading(true);
      
      try {
        // Geocode departure
        const depCoords = await geocodeAddress(departure);
        if (depCoords) {
          logger.debug('Departure geocoded:', departure, '→', depCoords);
          setDepartureCoords(depCoords);
        }

        // Geocode arrival
        const arrCoords = await geocodeAddress(arrival);
        if (arrCoords) {
          logger.debug('Arrival geocoded:', arrival, '→', arrCoords);
          setArrivalCoords(arrCoords);
        }

        // Geocode stops (in order)
        if (stops && stops.length > 0) {
          const sortedStops = [...stops].sort((a, b) => a.order - b.order);
          const geocodedStops = [];
          
          for (const stop of sortedStops) {
            // Use existing coordinates if available
            if (stop.coordinates && stop.coordinates.lat && stop.coordinates.lng) {
              geocodedStops.push({
                ...stop,
                coords: [stop.coordinates.lat, stop.coordinates.lng]
              });
            } else {
              // Geocode the address
              const coords = await geocodeAddress(stop.address || stop.city);
              if (coords) {
                geocodedStops.push({
                  ...stop,
                  coords
                });
              }
            }
          }
          
          logger.debug('Stops geocoded:', geocodedStops.length);
          setStopsCoords(geocodedStops);
        } else {
          setStopsCoords([]);
        }
      } catch (error) {
        console.error('Geocoding error:', error);
      } finally {
        setLoading(false);
      }
    };

    geocodeAll();
  }, [departure, arrival, JSON.stringify(stops)]);

  // Calculate route segments when coordinates change
  useEffect(() => {
    const calculateRoutes = async () => {
      // Build waypoints array: [departure, ...stops, arrival]
      const waypoints = [];
      
      if (departureCoords) {
        waypoints.push({ coords: departureCoords, label: 'Départ' });
      }
      
      stopsCoords.forEach((stop, index) => {
        if (stop.coords) {
          waypoints.push({ 
            coords: stop.coords, 
            label: `Étape ${stop.order || index + 1}`,
            city: stop.city || stop.address
          });
        }
      });
      
      if (arrivalCoords) {
        waypoints.push({ coords: arrivalCoords, label: 'Arrivée' });
      }

      // Need at least 2 points for a route
      if (waypoints.length < 2) {
        setRouteSegments([]);
        setMapBounds(null);
        return;
      }

      // Calculate bounds from all waypoints
      const allCoords = waypoints.map(wp => wp.coords);
      setMapBounds(allCoords);

      // Calculate route for each segment
      const segments = [];
      let totalDist = 0;

      for (let i = 0; i < waypoints.length - 1; i++) {
        const from = waypoints[i];
        const to = waypoints[i + 1];

        try {
          const response = await axios.post(`${API}/routing/calculate`, {
            departure_coords: from.coords,
            arrival_coords: to.coords,
            use_cache: true
          });

          if (response.data.success) {
            segments.push({
              from: from.label,
              to: to.label,
              coordinates: response.data.coordinates,
              distance: response.data.distance
            });
            totalDist += response.data.distance || 0;
          } else {
            // Fallback: straight line
            segments.push({
              from: from.label,
              to: to.label,
              coordinates: [from.coords, to.coords],
              distance: null
            });
          }
        } catch (error) {
          console.error(`Route error ${from.label} → ${to.label}:`, error.message);
          // Fallback: straight line
          segments.push({
            from: from.label,
            to: to.label,
            coordinates: [from.coords, to.coords],
            distance: null
          });
        }
      }

      setRouteSegments(segments);
      setTotalDistance(Math.round(totalDist));
      
      // Callback with total distance
      if (onDistanceCalculated && totalDist > 0) {
        onDistanceCalculated(Math.round(totalDist));
      }
    };

    if (departureCoords || arrivalCoords || stopsCoords.length > 0) {
      calculateRoutes();
    }
  }, [departureCoords, arrivalCoords, stopsCoords, onDistanceCalculated]);

  // Default center (Côte d'Ivoire)
  const defaultCenter = [7.539989, -5.547080];
  const center = departureCoords || arrivalCoords || defaultCenter;

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Distance indicator */}
      {showDistanceBadge && totalDistance > 0 && (
        <div className="absolute top-2 right-2 z-10 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-md border border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Distance:</span>
            <span className="font-bold text-[#1db584]">{totalDistance.toLocaleString('fr-FR')} km</span>
          </div>
          {stopsCoords.length > 0 && (
            <div className="text-xs text-gray-500 text-center">
              {routeSegments.length} segment{routeSegments.length > 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
          <div className="flex items-center gap-2 text-gray-600">
            <div className="w-4 h-4 border-2 border-[#1db584] border-t-transparent rounded-full animate-spin"></div>
            <span>Chargement de la carte...</span>
          </div>
        </div>
      )}

      <MapContainer
        center={center}
        zoom={6}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />

        {/* Fit bounds to show all markers */}
        {mapBounds && mapBounds.length >= 2 && (
          <FitBounds bounds={mapBounds} />
        )}

        {/* Departure marker (green) */}
        {departureCoords && (
          <Marker position={departureCoords} icon={startIcon}>
            <Popup>
              <div className="font-medium">🟢 Départ</div>
              <div className="text-sm text-gray-600">{departure}</div>
            </Popup>
          </Marker>
        )}

        {/* Intermediate stop markers (orange) */}
        {stopsCoords.map((stop, index) => (
          stop.coords && (
            <Marker key={`stop-${index}`} position={stop.coords} icon={stopIcon}>
              <Popup>
                <div className="font-medium">🟠 Étape {stop.order || index + 1}</div>
                <div className="text-sm text-gray-600">{stop.city || stop.address}</div>
                {stop.instructions && (
                  <div className="text-xs text-gray-500 mt-1 italic">"{stop.instructions}"</div>
                )}
              </Popup>
            </Marker>
          )
        ))}

        {/* Arrival marker (red) */}
        {arrivalCoords && (
          <Marker position={arrivalCoords} icon={endIcon}>
            <Popup>
              <div className="font-medium">🔴 Arrivée</div>
              <div className="text-sm text-gray-600">{arrival}</div>
            </Popup>
          </Marker>
        )}

        {/* Route polylines for each segment */}
        {routeSegments.map((segment, index) => (
          segment.coordinates && segment.coordinates.length > 0 && (
            <Polyline
              key={`route-${index}`}
              positions={segment.coordinates}
              color="#1db584"
              weight={4}
              opacity={0.8}
              dashArray={index > 0 ? "10, 10" : null}
            />
          )
        ))}
      </MapContainer>

      {/* Legend */}
      {(stopsCoords.length > 0 || departureCoords || arrivalCoords) && (
        <div className="absolute bottom-2 left-2 z-10 bg-white/95 backdrop-blur-sm px-2 py-1.5 rounded-lg shadow-sm text-xs flex items-center gap-3 border border-gray-100">
          {departureCoords && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Départ
            </span>
          )}
          {stopsCoords.length > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
              {stopsCoords.length} étape{stopsCoords.length > 1 ? 's' : ''}
            </span>
          )}
          {arrivalCoords && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
              Arrivée
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default TransportMap;
